import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Box, Typography } from '@mui/material';
import AccountsNavBarComponent from '../../components/AccountsNavBar/AccountsNavBarComponent';
import CollapsibleBoxComponent from '../../components/CollapsibleBox/CollapsibleBoxComponent';
import CommunicationPreferencesContent from './sub-screens/CommunicationPreferencesContent';
import PersonalDetailsContent from './sub-screens/PersonalDetailsContent';
import CommunicationDetailsTemplate from '../../json/components/subscreens/CommunicationDetailsTemplate.json';
import PersonalDetailsTemplate from '../../json/components/subscreens/PersonalDetailsTemplate.json';
import DriverDetailsContent from './sub-screens/DriverDetailsContent';
import PropertyDetailsContent from './sub-screens/PropertyDetailsContent';
import VehicleDetailsContent from './sub-screens/VehicleDetailsContent';
import PetDetailsContent from './sub-screens/PetDetailsContent';
import LoadingDots from '../../components/LoadingDots/LoadingDots';
import ScrollFadeWrapper from '../../components/ScrollFadeWrapper/ScrollFadeWrapper';
import { useMobileContext } from '../../contexts/MobileContext';
import { useLocation } from 'react-router-dom';
import SeoTagsManager from '../../utils/SeoTagsManager';
import {
  profileUpdatePerson,
  profileRetrievePerson,
  profileRetrieveVehicles,
  profileRetrieveVehicleDrivers,
  profileRetrievePropertyDetails,
  profileRetrievePetDetails,
} from '../../api/apiServices';
import useValidationRegistry from './useValidationRegistry';
import {
  ACCOUNT_DATA_UPDATED_EVENT,
  normalizeCellNumber,
  readAccountDataCache,
  syncAccountData,
} from '../../utils/accountDataCache';
import { withMinDuration } from '../../utils/withMinDuration';

const safeParse = (raw, fallback = {}) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const buildInitialValues = (data) =>
  Object.fromEntries(
    Object.entries(data || {}).map(([key, config]) => [
      key,
      config?.value ?? (config?.multiple === true ? [] : ''),
    ])
  );

const mapPersonPayloadToPersonalValues = (person = {}) => {
  const firstName = person?.FirstName?.trim() || '';
  const lastName = person?.LastName?.trim() || '';

  return {
    name: [firstName, lastName].filter(Boolean).join(' ').trim(),
    idNumber: person?.IDNumber || '',
    phone: person?.CellNumber || '',
    email: person?.EmailAddress || '',
    address: person?.FullAddress || '',
    maritalStatus: normalizeMaritalStatus(person?.MaritalStatus),
  };
};

const normalizeMaritalStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'cohabitating' || normalized === 'partneredcohabiting') {
    return 'Cohabitating';
  }

  if (
    normalized === 'separated' ||
    normalized === 'apartseperated' ||
    normalized === 'apartseparated'
  ) {
    return 'Separated';
  }

  if (normalized === 'single') return 'Single';
  if (normalized === 'married') return 'Married';
  if (normalized === 'divorced') return 'Divorced';
  if (normalized === 'widowed') return 'Widowed';

  return value || '';
};

const isTrueLike = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === 'true' ||
      normalized === '1' ||
      normalized === 'yes' ||
      normalized === 'on'
    );
  }
  return false;
};

const getChannelFlag = (channels = {}, channel) => {
  if (!channels || typeof channels !== 'object') return false;

  const directMatch = channels?.[channel];
  if (directMatch !== undefined) return isTrueLike(directMatch);

  const lowerChannel = channel.toLowerCase();
  const matchedKey = Object.keys(channels).find(
    (key) => String(key).toLowerCase() === lowerChannel
  );

  return matchedKey ? isTrueLike(channels[matchedKey]) : false;
};

const mapPersonPayloadToCommunicationValues = (person = {}) => {
  const channels = person?.MarketingChannels || {};
  const selectedChannels = ['Email', 'WhatsApp', 'SMS'].filter(
    (channel) =>
      getChannelFlag(channels, channel) ||
      isTrueLike(
        person?.[`MarketingChannel${channel === 'Email' ? 'Email' : channel}`]
      )
  );

  return {
    marketingComms: isTrueLike(person?.MarketingCommunication) ? 'On' : 'Off',
    channel: selectedChannels,
  };
};

const mapPersonPayloadToViewState = (person = {}) => ({
  personalValues: {
    ...buildInitialValues(PersonalDetailsTemplate),
    ...mapPersonPayloadToPersonalValues(person),
  },
  communicationValues: {
    ...buildInitialValues(CommunicationDetailsTemplate),
    ...mapPersonPayloadToCommunicationValues(person),
  },
});

const splitFullName = (fullName = '') => {
  const trimmed = String(fullName).trim();
  if (!trimmed) return { firstName: '', lastName: '' };

  const [firstName, ...rest] = trimmed.split(/\s+/);
  return {
    firstName: firstName || '',
    lastName: rest.join(' '),
  };
};

const buildUpdatePersonPayload = ({
  personValues,
  communicationValues,
  personRecord,
}) => {
  const { firstName, lastName } = splitFullName(personValues?.name);
  const selectedChannels = Array.isArray(communicationValues?.channel)
    ? communicationValues.channel
    : communicationValues?.channel
      ? [communicationValues.channel]
      : [];
  const marketingCommunication = isTrueLike(
    communicationValues?.marketingComms
  );

  return {
    personPK: personRecord?.PersonPK ?? 0,
    firstName,
    lastName,
    cellNumber: personValues?.phone || personRecord?.CellNumber || '',
    emailAddress: personValues?.email ?? personRecord?.EmailAddress ?? '',
    maritalStatus:
      normalizeMaritalStatus(
        personValues?.maritalStatus ?? personRecord?.MaritalStatus
      ) || '',
    gender: personRecord?.Gender ?? '',
    dateOfBirth: personRecord?.DateOfBirth ?? null,
    idNumber: personValues?.idNumber ?? personRecord?.IDNumber ?? '',
    fullAddress: personValues?.address ?? personRecord?.FullAddress ?? '',
    suburbPostalCode: personRecord?.SuburbPostalCode ?? '',
    suburbName: personRecord?.SuburbName ?? '',
    suburbSequenceNumber: personRecord?.SuburbSequenceNumber ?? 0,
    marketingCommunication,
    marketingChannelSMS: selectedChannels.includes('SMS'),
    marketingChannelWhatsApp: selectedChannels.includes('WhatsApp'),
    marketingChannelEmail: selectedChannels.includes('Email'),
  };
};

const persistLatestCellNumber = (cellNumber) => {
  const normalized = normalizeCellNumber(cellNumber);
  if (!normalized) return;

  const authSession = safeParse(localStorage.getItem('authSession'));
  localStorage.setItem(
    'authSession',
    JSON.stringify({ ...authSession, cellNumber: normalized })
  );
};

const resolveProfileCellNumber = () => {
  const authSession = safeParse(localStorage.getItem('authSession'));

  return normalizeCellNumber(authSession?.cellNumber || '');
};

// Account Avatar
const ACCOUNT_AVATAR = 'https://assets.askhippo.co.za/avatars/v2/Wave-v2.gif';

const TAB_TITLES = ['My Details', 'Drivers', 'Vehicles', 'Properties', 'Pets'];

const TAB_LOADING_DEFAULTS = {
  'My Details': false,
  Drivers: false,
  Vehicles: false,
  Pets: false,
  Properties: false,
};

function Accounts() {
  const { isMobile: isMobileRaw, isTablet } = useMobileContext();
  const isMobile = isMobileRaw || isTablet;
  const {
    registerValidation: registerMyDetailsValidation,
    validateAll: validateMyDetails,
  } = useValidationRegistry();
  const desktopImageColumnWidth = 248;
  const desktopColumnGap = 24;
  const desktopContentWidth = 912;
  const desktopLayoutWidth =
    desktopImageColumnWidth + desktopColumnGap + desktopContentWidth;
  const [tabIndex, setTabIndex] = useState(0);
  const personalDefaultValues = useMemo(
    () => buildInitialValues(PersonalDetailsTemplate),
    []
  );
  const communicationDefaultValues = useMemo(
    () => buildInitialValues(CommunicationDetailsTemplate),
    []
  );
  const [personalValues, setPersonalValues] = useState(personalDefaultValues);
  const [communicationValues, setCommunicationValues] = useState(
    communicationDefaultValues
  );
  const [personRecord, setPersonRecord] = useState(null);
  const [vehicleRecords, setVehicleRecords] = useState([]);
  const [driverRecords, setDriverRecords] = useState([]);
  const [propertyRecords, setPropertyRecords] = useState(null);
  const [petRecords, setPetRecords] = useState([]);
  const [tabLoading, setTabLoading] = useState(TAB_LOADING_DEFAULTS);
  const [savingMyDetailsSections, setSavingMyDetailsSections] = useState(
    () => new Set()
  );
  const [resettingMyDetailsSections, setResettingMyDetailsSections] = useState(
    () => new Set()
  );
  const { pathname } = useLocation();
  const activeTabTitle = TAB_TITLES[tabIndex];
  const isActiveTabLoading = tabLoading[activeTabTitle] === true;

  const handlePersonalChange = useCallback((name, value) => {
    setPersonalValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCommunicationChange = useCallback((name, value) => {
    setCommunicationValues((prev) => {
      if (name === 'channel') {
        const nextChannels = Array.isArray(value)
          ? value
          : value
            ? [value]
            : [];

        return {
          ...prev,
          channel: nextChannels,
        };
      }

      return { ...prev, [name]: value };
    });
  }, []);

  const resolveAccountCellNumber = useCallback(() => {
    const cachedSnapshot =
      readAccountDataCache(resolveProfileCellNumber()) ||
      readAccountDataCache('');
    const persistedCellNumber = normalizeCellNumber(
      personRecord?.CellNumber ||
        personRecord?.cellNumber ||
        personalValues?.phone ||
        cachedSnapshot?.cellNumber ||
        ''
    );

    return (
      persistedCellNumber ||
      normalizeCellNumber(cachedSnapshot?.cellNumber || '') ||
      normalizeCellNumber(resolveProfileCellNumber()) ||
      ''
    );
  }, [personalValues?.phone, personRecord?.CellNumber]);

  // The policy holder identifier sent on add-vehicle/add-driver requests must
  // match whatever identity the JWT is scoped to — prefer the authSession
  // values directly (and the channel — otpVia — that produced them) over any
  // value derived from the loaded profile/cache.
  const resolvePolicyHolderIdentifier = useCallback(() => {
    const authSession = safeParse(localStorage.getItem('authSession'), {});
    const isEmailAuth = authSession?.otpVia === 'email';

    return isEmailAuth
      ? authSession?.email || ''
      : authSession?.cellNumber || resolveAccountCellNumber();
  }, [resolveAccountCellNumber]);

  const applyAccountSnapshot = useCallback(
    (snapshot) => {
      const person = snapshot?.person || null;
      const vehicles = Array.isArray(snapshot?.vehicles)
        ? snapshot.vehicles
        : [];
      const drivers = Array.isArray(snapshot?.drivers) ? snapshot.drivers : [];
      const pets = Array.isArray(snapshot?.pets) ? snapshot.pets : [];
      const properties = snapshot?.properties || null;
      const cellNumber = normalizeCellNumber(
        person?.cellNumber || person?.CellNumber || snapshot?.cellNumber || ''
      );

      if (!person) {
        setPersonRecord(null);
        setPersonalValues(personalDefaultValues);
        setCommunicationValues(communicationDefaultValues);
      } else {
        const nextState = mapPersonPayloadToViewState(person);
        setPersonRecord(person);
        setPersonalValues(nextState.personalValues);
        setCommunicationValues(nextState.communicationValues);
      }

      setVehicleRecords(vehicles);
      setDriverRecords(drivers);
      setPropertyRecords(properties);
      setPetRecords(pets);

      if (cellNumber) {
        persistLatestCellNumber(cellNumber);
      }
    },
    [communicationDefaultValues, personalDefaultValues]
  );

  const refreshAccountData = useCallback(
    async (cellNumberOverride, options = {}) => {
      const showLoader = options?.showLoader !== false;
      // Callers specify which sections actually changed so we only hit the
      // endpoints that need refetching — e.g. saving a property shouldn't
      // also re-fetch vehicles, drivers and pets. "person" backs both the
      // My Details and Communication preferences sections (same API), so
      // those two are the only sections that share a refresh.
      const sections = options?.sections || [
        'person',
        'vehicles',
        'drivers',
        'properties',
        'pets',
      ];
      const sectionSet = new Set(sections);

      // An explicit override (e.g. the cell number just saved on "My Details")
      // always wins. Otherwise, look up via whichever channel the user
      // actually authenticated with — an email-based session may not have a
      // cell number resolved as its primary identifier.
      const authSession = safeParse(localStorage.getItem('authSession'), {});
      const isEmailAuth = authSession?.otpVia === 'email';
      // Email-authenticated sessions hold a JWT scoped to the email identifier —
      // any cell number override must not take precedence, or the JWT-protected
      // profile lookups will be made with a mismatched identifier and 401.
      const identifier = isEmailAuth
        ? authSession?.email || ''
        : cellNumberOverride
          ? normalizeCellNumber(cellNumberOverride)
          : resolveAccountCellNumber();

      if (!identifier) {
        applyAccountSnapshot(null);
        return false;
      }

      if (showLoader) {
        setTabLoading(
          Object.fromEntries(TAB_TITLES.map((title) => [title, true]))
        );
      }

      try {
        const [personRes, vehiclesRes, driversRes, propertiesRes, petsRes] =
          await Promise.all([
            sectionSet.has('person')
              ? profileRetrievePerson(identifier)
              : Promise.resolve(personRecord),
            sectionSet.has('vehicles')
              ? profileRetrieveVehicles(identifier)
              : Promise.resolve(vehicleRecords),
            sectionSet.has('drivers')
              ? profileRetrieveVehicleDrivers(identifier)
              : Promise.resolve(driverRecords),
            sectionSet.has('properties')
              ? profileRetrievePropertyDetails(identifier)
              : Promise.resolve(propertyRecords),
            sectionSet.has('pets')
              ? profileRetrievePetDetails(identifier).catch(() => null)
              : Promise.resolve(petRecords),
          ]);

        const resolvedCellNumber =
          normalizeCellNumber(personRes?.CellNumber || '') ||
          (isEmailAuth ? '' : identifier);

        const snapshot = await syncAccountData(
          resolvedCellNumber || identifier,
          {
            seedPerson: personRes,
            seedVehicles: vehiclesRes,
            seedDrivers: driversRes,
            seedProperties: propertiesRes,
            seedPets: petsRes,
          }
        );
        applyAccountSnapshot(snapshot);
        return true;
      } catch (error) {
        return false;
      } finally {
        if (showLoader) {
          setTabLoading(
            Object.fromEntries(TAB_TITLES.map((title) => [title, false]))
          );
        }
      }
    },
    [
      applyAccountSnapshot,
      driverRecords,
      personRecord,
      petRecords,
      propertyRecords,
      resolveAccountCellNumber,
      vehicleRecords,
    ]
  );

  const refreshVehicleData = useCallback(
    (cellNumberOverride, options = {}) =>
      refreshAccountData(cellNumberOverride, {
        ...options,
        sections: ['vehicles'],
      }),
    [refreshAccountData]
  );

  const refreshDriverData = useCallback(
    (cellNumberOverride, options = {}) =>
      refreshAccountData(cellNumberOverride, {
        ...options,
        sections: ['drivers'],
      }),
    [refreshAccountData]
  );

  const refreshPropertyData = useCallback(
    (cellNumberOverride, options = {}) =>
      refreshAccountData(cellNumberOverride, {
        ...options,
        sections: ['properties'],
      }),
    [refreshAccountData]
  );

  const refreshPetData = useCallback(
    (cellNumberOverride, options = {}) =>
      refreshAccountData(cellNumberOverride, {
        ...options,
        sections: ['pets'],
      }),
    [refreshAccountData]
  );

  const handleSave = useCallback(
    async (section) => {
      if (!validateMyDetails()) {
        return;
      }

      setSavingMyDetailsSections((prev) => new Set(prev).add(section));
      try {
        await withMinDuration(
          (async () => {
            const payload = buildUpdatePersonPayload({
              personValues: personalValues,
              communicationValues,
              personRecord,
            });

            await profileUpdatePerson(payload);
            persistLatestCellNumber(payload.cellNumber);
            await refreshAccountData(payload.cellNumber, {
              showLoader: false,
              forceRefresh: true,
              sections: ['person'],
            });
          })()
        );
      } catch (error) {
        // ignore
      } finally {
        setSavingMyDetailsSections((prev) => {
          const next = new Set(prev);
          next.delete(section);
          return next;
        });
      }
    },
    [
      communicationValues,
      personRecord,
      personalValues,
      refreshAccountData,
      validateMyDetails,
    ]
  );

  const handleSavePersonalDetails = useCallback(
    () => handleSave('personal'),
    [handleSave]
  );

  const handleSaveCommunicationDetails = useCallback(
    () => handleSave('communication'),
    [handleSave]
  );

  const handleReset = useCallback(
    async (section) => {
      setResettingMyDetailsSections((prev) => new Set(prev).add(section));
      try {
        await withMinDuration(
          refreshAccountData(undefined, {
            showLoader: false,
            forceRefresh: true,
            sections: ['person'],
          })
        );
      } finally {
        setResettingMyDetailsSections((prev) => {
          const next = new Set(prev);
          next.delete(section);
          return next;
        });
      }
    },
    [refreshAccountData]
  );

  const handleResetPersonalDetails = useCallback(
    () => handleReset('personal'),
    [handleReset]
  );

  const handleResetCommunicationDetails = useCallback(
    () => handleReset('communication'),
    [handleReset]
  );

  useEffect(() => {
    const hydrateFromAccountCache = () => {
      const authSession = safeParse(localStorage.getItem('authSession'), {});
      const cachedSnapshot =
        readAccountDataCache(authSession?.cellNumber || '') ||
        readAccountDataCache('');

      if (!cachedSnapshot) return false;

      applyAccountSnapshot(cachedSnapshot);
      return true;
    };

    hydrateFromAccountCache();

    const handleAccountDataUpdated = () => {
      hydrateFromAccountCache();
    };

    const handleStorage = (event) => {
      if (event?.key === 'accountDataCache' || event?.key === 'authSession') {
        hydrateFromAccountCache();
      }
    };

    window.addEventListener(
      ACCOUNT_DATA_UPDATED_EVENT,
      handleAccountDataUpdated
    );
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(
        ACCOUNT_DATA_UPDATED_EVENT,
        handleAccountDataUpdated
      );
      window.removeEventListener('storage', handleStorage);
    };
  }, [applyAccountSnapshot]);

  return (
    <>
      {SeoTagsManager.render(pathname)}
      <Container
        maxWidth={false}
        sx={{
          mt: { xs: 2, sm: 4 },
          px: { xs: 2, sm: 3, md: 3 },
          '@media (min-width: 1221px)': { px: 0 },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: isMobile ? '100%' : `${desktopLayoutWidth}px`,
            mx: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? '1fr'
                : `${desktopImageColumnWidth}px ${desktopContentWidth}px`,
              columnGap: isMobile ? 0 : `${desktopColumnGap}px`,
              alignItems: 'start',
            }}
          >
            {!isMobile && (
              <ScrollFadeWrapper>
                <Box
                  sx={{
                    width: `${desktopImageColumnWidth}px`,
                    pl: '33px',
                    pt: 0,
                    mt: '-24px',
                  }}
                >
                  <Box
                    component="img"
                    src={ACCOUNT_AVATAR}
                    alt="Hippo Avatar"
                    sx={{
                      width: '215px',
                      height: '215px',
                      opacity: 1,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </ScrollFadeWrapper>
            )}

            <Box
              sx={{
                width: '100%',
                maxWidth: isMobile ? '100%' : `${desktopContentWidth}px`,
                mx: 'auto',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <ScrollFadeWrapper>
                {isMobile ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      mb: 1.5,
                    }}
                  >
                    <Box
                      component="img"
                      src={ACCOUNT_AVATAR}
                      alt="Hippo Avatar"
                      sx={{
                        width: '138px',
                        height: '138px',
                        opacity: 1,
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                ) : null}

                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: isMobile ? '1.875rem' : '3.4375rem',
                    lineHeight: '1.1',
                    color: '#1d2b36',
                    textAlign: isMobile ? 'left' : 'center',
                  }}
                >
                  Welcome to Your Account
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: isMobile ? '0.9375rem' : '1.25rem',
                    lineHeight: '1.35',
                    color: '#1d2b36',
                    mt: 1,
                    textAlign: isMobile ? 'left' : 'center',
                  }}
                >
                  Here you can view and change all the details that help us
                  quote you better.
                </Typography>
              </ScrollFadeWrapper>

              <ScrollFadeWrapper sx={{ mt: 1.5, width: '100%' }}>
                <AccountsNavBarComponent
                  isMobile={isMobile}
                  value={tabIndex}
                  onChange={(event, newValue) => setTabIndex(newValue)}
                />
              </ScrollFadeWrapper>

              <ScrollFadeWrapper
                sx={{
                  mt: isMobile ? 1.5 : 2,
                  mb: isMobile ? 4 : 8,
                  width: '100%',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {isActiveTabLoading ? (
                  <Box
                    sx={{
                      minHeight: '180px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#1C2F3A',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: isMobile ? '1rem' : '1.25rem',
                    }}
                  >
                    <LoadingDots />
                  </Box>
                ) : activeTabTitle === 'My Details' ? (
                  <>
                    <PersonalDetailsContent
                      isMobile={isMobile}
                      handleSave={handleSavePersonalDetails}
                      resetValues={handleResetPersonalDetails}
                      values={personalValues}
                      onFieldChange={handlePersonalChange}
                      isSaving={savingMyDetailsSections.has('personal')}
                      isResetting={resettingMyDetailsSections.has('personal')}
                      registerValidation={registerMyDetailsValidation}
                    />
                    <CommunicationPreferencesContent
                      isMobile={isMobile}
                      handleSave={handleSaveCommunicationDetails}
                      resetValues={handleResetCommunicationDetails}
                      values={communicationValues}
                      onFieldChange={handleCommunicationChange}
                      isSaving={savingMyDetailsSections.has('communication')}
                      isResetting={resettingMyDetailsSections.has(
                        'communication'
                      )}
                    />
                  </>
                ) : activeTabTitle === 'Drivers' ? (
                  <DriverDetailsContent
                    isMobile={isMobile}
                    savedDriversData={driverRecords}
                    onResetDrivers={refreshDriverData}
                    policyHolderIdentifier={resolvePolicyHolderIdentifier()}
                  />
                ) : activeTabTitle === 'Vehicles' ? (
                  <VehicleDetailsContent
                    isMobile={isMobile}
                    savedVehiclesData={vehicleRecords}
                    onResetVehicles={refreshVehicleData}
                    policyHolderIdentifier={resolvePolicyHolderIdentifier()}
                  />
                ) : activeTabTitle === 'Properties' ? (
                  <PropertyDetailsContent
                    isMobile={isMobile}
                    savedPropertiesData={propertyRecords}
                    onResetProperties={refreshPropertyData}
                  />
                ) : activeTabTitle === 'Pets' ? (
                  <PetDetailsContent
                    isMobile={isMobile}
                    savedPetsData={petRecords}
                    onResetPets={refreshPetData}
                  />
                ) : (
                  <CollapsibleBoxComponent title={activeTabTitle}>
                    <Box sx={{ p: 2 }}>
                      <Typography>Coming soon...</Typography>
                    </Box>
                  </CollapsibleBoxComponent>
                )}
              </ScrollFadeWrapper>
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
}

export default Accounts;
